import React, { useState, useEffect, useRef } from 'react';
import AddressLookup from '../../templates/components/checkout/AddressLookup';
import { getAddressValidationClient } from '../../api/qas/getAddressValidationClient';
import { parseAugmentedAddress3 } from '../../utils/address-augmentation';
import { logError, logInfo } from '../../utils/logging';
import { formatAddress } from '../../utils/qas-addresses';
import { styles } from './GiftCardForm.css';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { transformGiftCardDataForTracking } from '../../utils/cart/transformGiftCardDataForTracking';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { ActionButton } from '@jbhi-fi/components/src/ActionButton';
import { CartActionState } from '@jbhi-fi/components/src/CartProvider/types';
import { ADD_TO_CART_CONFIRMATION_DURATION_MS } from '../../templates/components/pdp/callToAction/AddToCart';
import { postAlgoliaAddToCart } from '../../utils/algolia/search-insights';
import { updateCart } from '../../utils/cart/cartUtils';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { ShopifyStorefrontClient } from '../../api/shopify/ShopifyStorefrontClient';

const storefrontClient = new ShopifyStorefrontClient();

const CUSTOM_AMOUNT_OPTION = 'custom-amount';

const giftCardOptionsDisabled = window.Jbhifi?.giftCardOptionsDisabled;

const GiftCardFormRadio = ({ variant, setSelectedOption, selectedOption, setIsCustomAmount }) => (
  <li className={styles.radioInput}>
    <div className="radio__input">
      <input
        type="radio"
        data-price={variant.price}
        id={variant.id}
        className="input-radio"
        value={variant.price}
        name="id"
        onChange={() => {
          setSelectedOption(variant.price);
          setIsCustomAmount(false);
        }}
        checked={selectedOption === variant.price}
      />
    </div>
    <label htmlFor={variant.id} className="radio__label">
      {variant.title}
    </label>
  </li>
);

const CustomAmountInput = ({ customAmount, setCustomAmount }) => (
  <li id="custom-amount-input-wrap">
    <div className="jb-input--group">
      <div className="jb-input jb-input--has-prefix">
        <div className="prefix-wrapper">
          <div className="prefix">{getStoreCurrency()} $</div>
          <div className="prefixed">
            <input
              id="custom-amount-input"
              className="has-value"
              name="customamount"
              value={customAmount}
              onChange={event => {
                setCustomAmount(event.target.value);
              }}
              onBlur={event => {
                const newValue = Math.abs(parseInt(event.target.value));
                if (newValue && newValue >= 10 && newValue <= 500) {
                  setCustomAmount(newValue);
                  return;
                }

                const sanitizedNumber = newValue && newValue > 500 ? 500 : 10;
                event.target.value = sanitizedNumber;
                setCustomAmount(sanitizedNumber);
              }}
              type="number"
              step="1"
              min="10"
              max="500"
              minLength="2"
              maxLength="3"
              pattern="\d+"
              required
            />
            <label htmlFor="custom-amount-input">Value</label>
            <div className="prefixed-frame"></div>
          </div>
        </div>
        <div id="custom-errors"></div>
      </div>
    </div>
  </li>
);

const DeliveryOptions = ({ delivery, setDelivery }) => (
  <>
    <h2>Delivery details</h2>
    {!giftCardOptionsDisabled ? (
      <>
        <ul id="sendby-options" className="radios-list">
          <li className={styles.radioInput}>
            <div className="radio__input">
              <input
                type="radio"
                className="input-radio"
                name="properties[Delivery]"
                id="sendByEmail"
                value="digital"
                onChange={() => {
                  setDelivery('digital');
                }}
                checked={delivery === 'digital'}
              />
            </div>
            <label htmlFor="sendByEmail" className="radio__label">
              Send by email
            </label>
          </li>
          <li className={styles.radioInput}>
            <div className="radio__input">
              <input
                type="radio"
                className="input-radio"
                name="properties[Delivery]"
                id="sendByPost"
                onChange={() => {
                  setDelivery('physical');
                }}
                value="physical"
                checked={delivery === 'physical'}
              />
            </div>
            <label htmlFor="sendByPost" className="radio__label">
              Send by post
            </label>
          </li>
        </ul>
      </>
    ) : null}
  </>
);

const InputError = ({ errorMessage }) => <span className="field__input-error">{errorMessage}</span>;

/**
 * Using the delivery method and amount, find the corresponding variant ID to be added to cart.
 */
const getGiftCardVariantId = async (delivery, amount) => {
  const url = `/products/gift-card-${delivery}-${amount}.json`;
  logInfo(url, 'Getting gift card variant');

  try {
    const giftCardProductResponse = await fetch(url);

    const giftCardProductData = await giftCardProductResponse.json();

    const variant = giftCardProductData.product.variants.find(card => card.price === amount.toFixed(2));

    if (!variant) {
      logError('Could not find matching variant');
      return false;
    }

    return variant.id;
  } catch (error) {
    logError(error, 'Error getting gift card variant');
    return false;
  }
};

const GiftCardForm = ({ giftCardProduct }) => {
  const firstNameInputRef = useRef(null);
  const lastNameInputRef = useRef(null);
  const emailInputRef = useRef(null);
  const addressInputRef = useRef(null);
  const cityInputRef = useRef(null);
  const stateInputRef = useRef(null);
  const postcodeInputRef = useRef(null);
  const manualAddressInputRef = useRef(null);

  const [selectedOption, setSelectedOption] = useState(giftCardProduct.variants[0].price);
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState(10);

  const [delivery, setDelivery] = useState('digital');
  const [selectedAddress, setSelectedAddress] = useState('');

  const [isManualAddress, setIsManualAddress] = useState(false);
  const [isBusinessAddress, setIsBusinessAddress] = useState(false);

  const [formFields, setFormFields] = useState({});
  const [formFieldsErrors, setFormFieldsErrors] = useState({});
  const [formIsValid, setFormIsValid] = useState(false);

  const [ctaState, setCtaState] = useState(CartActionState.Default);

  /**
   * Checks the required fields depending on the delivery type are present and have a value.
   */
  const checkFormValidity = () => {
    const requiredGeneralFields = ['_first_name', '_last_name'];
    const requiredDigitalFields = ['_email'];
    const requiredPhysicalFields = ['_address', '_city', '_state', '_postcode'];

    const requiredFields =
      delivery === 'physical'
        ? [...requiredGeneralFields, ...requiredPhysicalFields]
        : [...requiredGeneralFields, ...requiredDigitalFields];

    const allRequiredFieldsValid = requiredFields.every(requiredFieldKey => {
      const formHasRequiredField = Object.keys(formFields).includes(requiredFieldKey);
      const requiredFieldHasValue = formHasRequiredField && Boolean(formFields[requiredFieldKey]);

      if (requiredFieldKey === '_address' && !requiredFieldHasValue && !isManualAddress) {
        setFormFieldsErrors(formFieldsErrors => ({
          ...formFieldsErrors,
          [requiredFieldKey]: 'Address required',
        }));
        return false;
      }
      return formHasRequiredField && requiredFieldHasValue;
    });
    return allRequiredFieldsValid;
  };

  /**
   * Checks the input validity using the native checkValidity method.
   * Passed to input blur and input events so we can pass the event.target object.
   *
   * If validity fails, sets the appropriate formFieldsError key.
   */
  const checkInputValidity = (eventTarget, fieldKey, errorMessage) => {
    if (fieldKey === '_address' && !checkFormValidity() && !isManualAddress) {
      setFormFieldsErrors(formFieldsErrors => ({
        ...formFieldsErrors,
        [fieldKey]: errorMessage,
      }));
    } else {
      if (!eventTarget.checkValidity()) {
        setFormFieldsErrors(formFieldsErrors => ({
          ...formFieldsErrors,
          [fieldKey]: errorMessage,
        }));
      } else {
        setFormFieldsErrors(formFieldsErrors => ({
          ...formFieldsErrors,
          [fieldKey]: null,
        }));
      }
    }
  };

  /**
   * Sets the formFields key using the corresponding value.
   */
  const setInputValue = (fieldKey, value) => {
    setFormFields(formFields => ({
      ...formFields,
      [fieldKey]: value,
    }));
  };

  /**
   * Resets all object values to an empty string '';
   */
  const resetObjectValues = object => Object.fromEntries(Object.keys(object).map(key => [key, '']));

  /**
   * Resets all relevant state values to the intial state.
   */
  const resetForm = () => {
    setFormFields(formFields => resetObjectValues(formFields));
    setFormFieldsErrors(formFieldsErrors => resetObjectValues(formFieldsErrors));
    setSelectedOption(giftCardProduct.variants[0].price);
    setIsCustomAmount(false);
    setCustomAmount(10);
    setDelivery('digital');
    setSelectedAddress('');
    setIsManualAddress(false);
    setIsBusinessAddress(false);
  };

  /**
   * Handles the form submission.
   *
   * 1. Sets the CTA state to loading.
   * 2. Finds the corresponding variant ID to be added to cart using the delivery type and amount.
   * 3. Creates a general properties object that applies to both digital and physical gift cards.
   * 4. Adds delivery specific properties.
   * 5. Add the variant ID to cart using the properties object.
   */
  const handleFormSubmit = async event => {
    setCtaState(CartActionState.Updating);

    /**
     * Find the variant ID using delivery type and amount.
     * The predefined amounts are in cents (e.g 5000 for $50), so we divide
     * by 100 to get the dollar amount like the custom amount.
     */
    const giftCardVariantId = await getGiftCardVariantId(
      delivery,
      isCustomAmount ? customAmount : selectedOption / 100
    );

    const {
      _first_name,
      _last_name,
      _email,
      _address,
      _address2,
      _postcode,
      _state,
      _city,
      _qas_address,
      _business_name,
      _message,
      _from,
    } = formFields;

    /**
     * Create a properties object with the general keys that apply to both digital and physical
     * gift cards.
     *
     * _message and _from are optional and are conditionally added to the object.
     */
    let properties = {
      _first_name,
      _last_name,
      _gift_card_timestamp: Date.now(),
      ...(_message && { _message }),
      ...(_from && { _from }),
      Recipient: `${formFields._first_name} ${formFields._last_name}`,
      Delivery: delivery[0].toUpperCase() + delivery.slice(1),
    };

    /**
     * If delivery type is digital, add the relevant keys.
     */
    if (delivery === 'digital') {
      properties = {
        ...properties,
        _email,
        'Emailing to': _email,
      };
    }

    /**
     * If delivery type is physical, add the relevant keys.
     *
     * _address2, _qas_address and _business_name are optional and are conditionally added to the object.
     */
    if (delivery === 'physical') {
      properties = {
        ...properties,
        _address,
        ...(_address2 && { _address2 }),
        _city,
        _state,
        _postcode,
        ...(_qas_address && { _qas_address }),
        ...(_business_name && { _business_name }),
        'Posting to': formatAddress({
          business_name: _business_name,
          address: _address,
          address2: _address2,
          city: _city,
          state: _state,
          postcode: _postcode,
        }),
      };
    }

    const cart = await storefrontClient.addToCart(giftCardVariantId, 1, filterAndMapAttributes(properties));
    if (cart) {
      const data = cart.items.find(item => item.variant_id === giftCardVariantId.toString());
      postAlgoliaAddToCart(data.sku);
      trackAddToCart({
        location: 'giftCard',
        currency: getStoreCurrency(),
        addedItems: transformGiftCardDataForTracking(data, 'giftCardForm'),
      });
      await updateCart(cart);
      setCtaState(CartActionState.Complete);
      setTimeout(() => {
        setCtaState(CartActionState.Default);
      }, ADD_TO_CART_CONFIRMATION_DURATION_MS);
      resetForm();
    } else {
      console.error('Error adding gift card to cart');
      resetForm();
    }
  };

  /**
   * Use effect triggered when any of the form fields or delivery methods are updated.
   * Controls the formIsValid boolean which enables/disables the add to cart button.
   */
  useEffect(() => setFormIsValid(), [formFields, delivery]);

  const addressValidationClient = getAddressValidationClient();

  const handleAddToCartClick = event => {
    event.preventDefault();

    // Manually trigger onInput events for required fields
    if (firstNameInputRef.current) {
      firstNameInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (lastNameInputRef.current) {
      lastNameInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (emailInputRef.current && delivery === 'digital') {
      emailInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (cityInputRef.current) {
      cityInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (stateInputRef.current) {
      stateInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (postcodeInputRef.current) {
      postcodeInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (manualAddressInputRef.current) {
      manualAddressInputRef.current.dispatchEvent(new Event('input', { bubbles: true }));
    }
    if (!giftCardOptionsDisabled && addressInputRef.current) {
      const isAddressValid = addressInputRef.current.validateAddress();
      !isAddressValid &&
        setFormFieldsErrors(formFieldsErrors => ({
          ...formFieldsErrors,
          ['_address']: 'Address required',
        }));
    }

    // Check form validity and proceed with adding to cart if valid
    if (checkFormValidity()) {
      handleFormSubmit();
    }
  };

  useEffect(() => {
    const giftCardSelectedValue = new URLSearchParams(window.location.search).get('value');
    if (giftCardSelectedValue) {
      const centsValue = Number(giftCardSelectedValue) * 100;
      const variant = giftCardProduct.variants.find(variant => variant.price === centsValue);
      if (variant) {
        setSelectedOption(centsValue);
      } else {
        setSelectedOption(CUSTOM_AMOUNT_OPTION);
        setIsCustomAmount(true);
        setCustomAmount(Number(giftCardSelectedValue));
      }
    }
  }, [giftCardProduct.variants]);

  return (
    <form
      action="/cart/add"
      method="post"
      autoComplete="off"
      className="qas-form scoped-form-styles"
      onSubmit={handleFormSubmit}
    >
      <h2>Choose Value</h2>
      <ul className="radios-list">
        {giftCardProduct.variants.map(variant => {
          if (!variant.available) return null;

          return (
            <GiftCardFormRadio
              key={variant.id}
              setSelectedOption={setSelectedOption}
              selectedOption={selectedOption}
              setIsCustomAmount={setIsCustomAmount}
              variant={variant}
            />
          );
        })}
        <li className={`custom-amount-option ${styles.radioInput}`}>
          <div className="radio__input">
            <input
              type="radio"
              data-price="0.00"
              id="custom-amount"
              className="input-radio"
              value="custom-amount"
              onChange={() => {
                setSelectedOption(CUSTOM_AMOUNT_OPTION);
                setIsCustomAmount(true);
              }}
              checked={selectedOption === 'custom-amount'}
              name="id"
            />
          </div>
          <label htmlFor="custom-amount" className="radio__label">
            Nominate value (between $10 and $500)
          </label>
        </li>

        {isCustomAmount ? <CustomAmountInput customAmount={customAmount} setCustomAmount={setCustomAmount} /> : null}
      </ul>

      <DeliveryOptions delivery={delivery} setDelivery={setDelivery} />

      <div className="grid">
        <div className="grid__item large-up--one-half">
          <div className="jb-input">
            <input
              id="first"
              name="properties[_first_name]"
              type="text"
              placeholder="e.g. Alex"
              pattern="^[A-Za-z- ']*$"
              required
              onInput={event => {
                setInputValue('_first_name', event.target.value);
                checkInputValidity(event.target, '_first_name', 'First Name required');
              }}
              onBlur={event => checkInputValidity(event.target, '_first_name', 'First Name required')}
              value={formFields._first_name}
              className={formFields._first_name ? 'has-value' : ''}
              ref={firstNameInputRef}
            />
            <label className="required" htmlFor="first">
              Recipient's first name
            </label>
            {formFieldsErrors._first_name ? <InputError errorMessage={formFieldsErrors._first_name} /> : null}
          </div>
        </div>

        <div className="grid__item large-up--one-half">
          <div className="jb-input">
            <input
              id="last"
              name="properties[_last_name]"
              type="text"
              placeholder="e.g. Smith"
              required
              pattern="^[A-Za-z- ']*$"
              onInput={event => {
                setInputValue('_last_name', event.target.value);
                checkInputValidity(event.target, '_last_name', 'Last Name required');
              }}
              onBlur={event => checkInputValidity(event.target, '_last_name', 'Last Name required')}
              value={formFields._last_name}
              className={formFields._last_name ? 'has-value' : ''}
              ref={lastNameInputRef}
            />
            <label className="required" htmlFor="last">
              Recipient's last name
            </label>
            {formFieldsErrors._last_name ? <InputError errorMessage={formFieldsErrors._last_name} /> : null}
          </div>
        </div>
      </div>

      {delivery === 'digital' && (
        <div id="sendByEmailContent" className="jb-input">
          <input
            id="email"
            name="properties[_email]"
            type="email"
            placeholder="example@domain.com"
            required
            onInput={event => {
              setInputValue('_email', event.target.value);
              checkInputValidity(event.target, '_email', 'Email required');
            }}
            onBlur={event => checkInputValidity(event.target, '_email', 'Email required')}
            value={formFields._email}
            className={formFields._email ? 'has-value' : ''}
            ref={emailInputRef}
          />
          <label className="required" htmlFor="email">
            Recipient's email address
          </label>
          {formFieldsErrors._email ? <InputError errorMessage={formFieldsErrors._email} /> : null}
        </div>
      )}

      {delivery === 'physical' && (
        <div id="sendByPostContent">
          {!isManualAddress && (
            <>
              <AddressLookup
                initialQuery=""
                initialOption={selectedAddress}
                handleInputChanged={addressValidationClient.getAddressSearch}
                handleInputBlured={event => {
                  checkInputValidity(event.target, '_address', 'Address required');
                }}
                handleSuggestionSelected={async (address, addressString) => {
                  setSelectedAddress(addressString);
                  try {
                    const { data } = await addressValidationClient.getAddressDetailById(address.Id);

                    const { Line1, Line2, Postcode, Suburb, CityState } = data.message;

                    const parsedAddress2 = parseAugmentedAddress3(Line2 || '');
                    const displayedAddress2 = parsedAddress2.address !== '' ? `${parsedAddress2.address}, ` : '';

                    const addressString = `${Line1}, ${displayedAddress2}${Suburb}, ${CityState}, ${Postcode}`;

                    /**
                     * Set all the relevant physical delivery fields.
                     */
                    setFormFields(formFields => ({
                      ...formFields,
                      _address: Line1,
                      _address2: Line2,
                      _city: Suburb,
                      _state: CityState,
                      _postcode: Postcode,
                      _qas_address: addressString,
                    }));

                    setFormFieldsErrors(formFieldsErrors => ({
                      ...formFieldsErrors,
                      _address: null,
                    }));
                  } catch (error) {
                    logError(error);
                  }
                }}
                handleCleared={() => {
                  /**
                   * Reset the physical delivery fields.
                   */
                  setFormFields(formFields => {
                    const { _address, _address2, _city, _state, _postcode, _qas_address, ...newFormFields } =
                      formFields;

                    return newFormFields;
                  });
                }}
                handleManualClick={() => {
                  setIsManualAddress(true);
                  /**
                   * Reset the physical delivery fields.
                   */
                  setFormFields(formFields => {
                    const { _address, _address2, _city, _state, _postcode, _qas_address, ...newFormFields } =
                      formFields;

                    return newFormFields;
                  });
                  setSelectedAddress('');
                }}
                manualClickLabel="Manual address"
                formatSuggestion={({ MatchedAddress }) => MatchedAddress}
                inputLabel="Address"
                inputPlaceholder="Start typing your address"
                minQueryLength={6}
                ref={addressInputRef}
              />
              {formFieldsErrors._address ? <InputError errorMessage={formFieldsErrors._address} /> : null}
            </>
          )}

          {isManualAddress ? (
            <>
              <a
                className="qas-enable-button"
                onClick={() => {
                  setIsManualAddress(false);
                  setFormFields(formFields => {
                    const { _address, _address2, _city, _state, _postcode, _qas_address, ...newFormFields } =
                      formFields;

                    return newFormFields;
                  });
                }}
              >
                Go back to address lookup
              </a>
              <div className="row row-address1  jb-input">
                <input
                  id="address"
                  name="properties[_address]"
                  type="text"
                  placeholder="e.g. 123 Sample Street"
                  required
                  onInput={event => {
                    setInputValue('_address', event.target.value);
                    checkInputValidity(event.target, '_address', 'Address required');
                  }}
                  onBlur={event => checkInputValidity(event.target, '_address', 'Address required')}
                  value={formFields._address}
                  className={formFields._address ? 'has-value' : ''}
                  ref={manualAddressInputRef}
                />
                <label className="required" htmlFor="address">
                  Address
                </label>
                {formFieldsErrors._address ? <InputError errorMessage={formFieldsErrors._address} /> : null}
              </div>
              <div className="row row-address2  jb-input">
                <input
                  id="address2"
                  name="properties[_address2]"
                  type="text"
                  onInput={event => {
                    setInputValue('_address2', event.target.value);
                  }}
                />
                <label htmlFor="address2">Address line 2</label>
              </div>
              <div className="row row-city  jb-input">
                <input
                  id="city"
                  name="properties[_city]"
                  type="text"
                  placeholder="e.g. Smallville"
                  required
                  pattern="^[A-Za-z ]*$"
                  onInput={event => {
                    setInputValue('_city', event.target.value);
                    checkInputValidity(event.target, '_city', 'City required');
                  }}
                  onBlur={event => checkInputValidity(event.target, '_city', 'City required')}
                  value={formFields._city}
                  className={formFields._city ? 'has-value' : ''}
                  ref={cityInputRef}
                />
                <label className="required" htmlFor="city">
                  City
                </label>
                {formFieldsErrors._city ? <InputError errorMessage={formFieldsErrors._city} /> : null}
              </div>

              <div className="grid">
                <div className="grid__item large-up--one-half">
                  <div className="row-province  jb-input" id="AddressProvinceContainerGiftcards">
                    <input
                      type="text"
                      id="AddressProvinceGiftcards"
                      name="properties[_state]"
                      pattern="^[A-Za-z ]*$"
                      required
                      onInput={event => {
                        setInputValue('_state', event.target.value);
                        checkInputValidity(event.target, '_state', 'State or territory required');
                      }}
                      onBlur={event => checkInputValidity(event.target, '_state', 'State or territory required')}
                      value={formFields._state}
                      className={formFields._state ? 'has-value' : ''}
                      ref={stateInputRef}
                    />
                    <label className="required" htmlFor="AddressProvinceGiftcards">
                      State or territory
                    </label>
                    {formFieldsErrors._state ? <InputError errorMessage={formFieldsErrors._state} /> : null}
                  </div>
                </div>
                <div className="grid__item large-up--one-half">
                  <div className="row-zip  jb-input">
                    <input
                      id="postcode"
                      name="properties[_postcode]"
                      type="number"
                      placeholder="e.g. 3000"
                      pattern="\d+"
                      required
                      onInput={event => {
                        setInputValue('_postcode', event.target.value);
                        checkInputValidity(event.target, '_postcode', 'Postcode required');
                      }}
                      onBlur={event => checkInputValidity(event.target, '_postcode', 'Postcode required')}
                      value={formFields._postcode}
                      className={formFields._postcode ? 'has-value' : ''}
                      ref={postcodeInputRef}
                    />
                    <label className="required" htmlFor="postcode">
                      Postcode
                    </label>
                    {formFieldsErrors._postcode ? <InputError errorMessage={formFieldsErrors._postcode} /> : null}
                  </div>
                </div>
              </div>
            </>
          ) : null}
          <div className="row row-showbusinessname checkbox-wrapper">
            <div className="checkbox__input">
              <input
                name="showbusinessname"
                type="checkbox"
                id="business-check"
                className="input-checkbox"
                onChange={() => {
                  setIsBusinessAddress(isBusinessAddress => !isBusinessAddress);
                }}
                checked={isBusinessAddress}
              />
            </div>
            <label className="checkbox__label" htmlFor="business-check">
              Is this a business address?
            </label>
          </div>

          {isBusinessAddress ? (
            <div className="row row-businessname  jb-input" id="business-show">
              <input
                id="businessname"
                name="properties[_business_name]"
                type="text"
                placeholder="e.g. SampleName Pty Ltd"
                onInput={event => {
                  setInputValue('_business_name', event.target.value);
                }}
                value={formFields._business_name}
                className={formFields._business_name ? 'has-value' : ''}
              />
              <label htmlFor="businessname">Company name</label>
            </div>
          ) : null}
        </div>
      )}

      <div className="jb-input">
        <input
          id="from"
          name="properties[_from]"
          type="text"
          placeholder="Alex Smith"
          onInput={event => {
            setInputValue('_from', event.target.value);
          }}
          value={formFields._from}
          className={formFields._from ? 'has-value' : ''}
        />
        <label htmlFor="from">From full name</label>
      </div>

      <div className="jb-input ">
        <textarea
          id="personalmsg"
          name="properties[_message]"
          maxLength="500"
          data-error-msg="Message cannot be more than 500 characters"
          placeholder="e.g. Happy holidays!"
          onInput={event => {
            setInputValue('_message', event.target.value);
          }}
          value={formFields._message}
          className={formFields._message ? 'has-value' : ''}
        />
        {formFields?._message?.length ? 500 - formFields._message.length : 500} characters remaining
        <label htmlFor="personalmsg">Personal message </label>
      </div>

      <div className="custom-add-to-cart">
        <ActionButton
          label="Add to cart"
          onClick={handleAddToCartClick}
          isLoading={ctaState === CartActionState.Updating}
          state={ctaState}
          actionFinishedLabel="Added!"
          size="sm"
          dataTestId="gift-card-to-cart"
        />
      </div>
    </form>
  );
};

export default GiftCardForm;
